import React, { useEffect, useMemo, useState } from 'react'
import { useLang } from '../lib/useLang'

const TYPE_OPTIONS = ['string', 'int', 'float', 'bool', 'date', 'categorical']

function ColumnRow({ column, index, onUpdate, onRemove }) {
  const { t } = useLang()
  return (
    <div
      className='group relative rounded-xl p-4 transition-all duration-200'
      style={{
        background: 'rgba(14, 14, 16, 0.02)',
        border: '1px solid rgba(14, 14, 16, 0.04)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'rgba(14, 14, 16, 0.07)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'rgba(14, 14, 16, 0.04)'
      }}
    >
      <div className='flex items-start gap-4'>
        {/* Column Name */}
        <div className='flex-1'>
          <label className='mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-ink'>
            {t('columnEditor.columnName')}
          </label>
          <input
            type='text'
            value={column.name || ''}
            onChange={e => onUpdate('name', e.target.value)}
            placeholder={t('columnEditor.namePlaceholder')}
            className='mod-field w-full rounded-lg px-3 py-2.5 text-sm text-ink placeholder-ink/40'
          />
        </div>

        {/* Type Select */}
        <div className='w-32'>
          <label className='mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-ink'>
            {t('columnEditor.type')}
          </label>
          <div className='relative'>
            <select
              value={column.type || 'string'}
              onChange={e => onUpdate('type', e.target.value)}
              className='mod-field w-full appearance-none rounded-lg px-3 py-2.5 pr-8 text-sm text-ink'
            >
              {TYPE_OPTIONS.map(type => (
                <option key={type} value={type} className='bg-white text-ink'>
                  {type}
                </option>
              ))}
            </select>
            <svg
              width='12'
              height='12'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink'
            >
              <path d='m6 9 6 6 6-6' />
            </svg>
          </div>
        </div>

        {/* Remove Button */}
        <button
          onClick={onRemove}
          className='mt-6 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-ink transition-all hover:bg-red-500/10 hover:text-red-600'
        >
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <line x1='18' y1='6' x2='6' y2='18' />
            <line x1='6' y1='6' x2='18' y2='18' />
          </svg>
        </button>
      </div>

      {/* Description */}
      <div className='mt-3'>
        <label className='mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-ink'>
          {t('columnEditor.description')}
        </label>
        <input
          type='text'
          value={column.description || ''}
          onChange={e => onUpdate('description', e.target.value)}
          placeholder={t('columnEditor.descPlaceholder')}
          className='mod-field w-full rounded-lg px-3 py-2.5 text-sm text-ink placeholder-ink/40'
        />
      </div>

      {/* Index Badge */}
      <div className='absolute -left-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-ink/[0.03] text-[10px] font-medium text-ink'>
        {index + 1}
      </div>
    </div>
  )
}

export default function ColumnEditor({ value = [], onChange }) {
  const { t } = useLang()
  const [rows, setRows] = useState(() => (Array.isArray(value) ? value : []))

  useEffect(() => {
    const incoming = Array.isArray(value) ? value : []
    if (JSON.stringify(incoming) !== JSON.stringify(rows)) {
      setRows(incoming)
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateRows = newRows => {
    setRows(newRows)
    if (onChange) onChange(newRows)
  }

  const addRow = () => updateRows([...rows, { name: '', type: 'string', description: '' }])
  const removeRow = idx => updateRows(rows.filter((_, i) => i !== idx))
  const upd = (idx, key, val) => updateRows(rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)))

  const hasDupNames = useMemo(() => {
    const names = rows.map(r => (r.name || '').trim()).filter(Boolean)
    const set = new Set()
    for (const n of names) {
      if (set.has(n)) return true
      set.add(n)
    }
    return false
  }, [rows])

  const importFromText = () => {
    const text = window.prompt(t('columnEditor.importPrompt'), '')
    if (!text) return
    const out = []
    text
      .split('\n')
      .map(l => l.trim())
      .filter(Boolean)
      .forEach(l => {
        const parts = l.split(':')
        const name = (parts[0] || '').trim()
        if (!name) return
        const type = (parts[1] || 'string').trim()
        const description = (parts.slice(2).join(':') || '').trim()
        out.push({ name, type, description })
      })
    if (out.length) updateRows(out)
  }

  const exportToText = async () => {
    const text = rows
      .map(r => `${r.name || ''}:${r.type || 'string'}:${r.description || ''}`.replace(/:+$/, ''))
      .join('\n')
    try {
      await navigator.clipboard.writeText(text)
      alert(t('columnEditor.copiedClipboard'))
    } catch {
      window.prompt(t('columnEditor.copyManually'), text)
    }
  }

  return (
    <div
      className='h-full rounded-2xl'
      style={{
        background: 'rgba(14, 14, 16, 0.01)',
        border: '1px solid rgba(14, 14, 16, 0.04)',
      }}
    >
      {/* Header */}
      <div
        className='flex items-center justify-between px-6 py-4'
        style={{ borderBottom: '1px solid rgba(14, 14, 16, 0.04)' }}
      >
        <div>
          <h3 className='text-sm font-medium text-ink'>{t('columnEditor.columnSchema')}</h3>
          <p className='mt-0.5 text-xs text-ink'>
            {t('columnEditor.columnsDefined', { count: rows.length })}
          </p>
        </div>

        {/* Action Buttons */}
        <div className='flex items-center gap-2'>
          <button
            onClick={importFromText}
            className='flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-ink transition-colors hover:bg-ink/[0.05] hover:text-ink'
          >
            <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
              <polyline points='17,8 12,3 7,8' />
              <line x1='12' y1='3' x2='12' y2='15' />
            </svg>
            {t('columnEditor.import')}
          </button>
          <button
            onClick={exportToText}
            className='flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-ink transition-colors hover:bg-ink/[0.05] hover:text-ink'
          >
            <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
              <polyline points='7,10 12,15 17,10' />
              <line x1='12' y1='15' x2='12' y2='3' />
            </svg>
            {t('columnEditor.export')}
          </button>
        </div>
      </div>

      {/* Column List */}
      <div className='space-y-3 p-5'>
        {rows.length === 0 ? (
          <div className='flex h-32 flex-col items-center justify-center text-center'>
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.5'
              className='mb-2 text-ink'
            >
              <rect x='3' y='3' width='7' height='7' />
              <rect x='14' y='3' width='7' height='7' />
              <rect x='14' y='14' width='7' height='7' />
              <rect x='3' y='14' width='7' height='7' />
            </svg>
            <p className='text-xs text-ink'>{t('columnEditor.noColumns')}</p>
          </div>
        ) : (
          rows.map((row, idx) => (
            <ColumnRow
              key={idx}
              column={row}
              index={idx}
              onUpdate={(key, val) => upd(idx, key, val)}
              onRemove={() => removeRow(idx)}
            />
          ))
        )}
      </div>

      {/* Add Column Button */}
      <div className='px-5 pb-5'>
        <button
          onClick={addRow}
          className='flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-medium transition-all'
          style={{
            background: 'rgba(255, 90, 31, 0.08)',
            border: '1px solid rgba(255, 90, 31, 0.15)',
            color: 'rgba(255, 90, 31, 0.9)',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(255, 90, 31, 0.12)'
            e.currentTarget.style.borderColor = 'rgba(255, 90, 31, 0.25)'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'rgba(255, 90, 31, 0.08)'
            e.currentTarget.style.borderColor = 'rgba(255, 90, 31, 0.15)'
          }}
        >
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <path d='M12 5v14M5 12h14' />
          </svg>
          {t('columnEditor.addColumn')}
        </button>

        {hasDupNames && (
          <div className='mt-3 flex items-center gap-2 rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-600'>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <circle cx='12' cy='12' r='10' />
              <line x1='12' y1='8' x2='12' y2='12' />
              <line x1='12' y1='16' x2='12.01' y2='16' />
            </svg>
            {t('columnEditor.duplicateNames')}
          </div>
        )}
      </div>
    </div>
  )
}
